import type React from 'react';
import { Fragment } from 'react';
import { Controller, FormProvider, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { zodResolver } from '@hookform/resolvers/zod';
import { X } from 'lucide-react';
import { z } from 'zod';

import { closeModal } from '@/components/modals/modals.utils';
import { ModalBase } from '@/components/modals/modals-base';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { cn } from '@/lib/utils';

const schema = z.object({
  salesPrice: z.boolean(),
  rentalPrice: z.boolean(),
  confidence: z.boolean(),
  salesPriceRange: z.boolean(),
  rentalPriceRange: z.boolean(),
  coordinates: z.boolean(),
  currency: z.boolean(),
  offerStatistics: z.boolean(),
  pricingHeatMap: z.boolean(),
  offerSearch: z.boolean(),
  offers: z.boolean(),
  socioEconomics: z.boolean(),
  pointsOfInterest: z.boolean(),
  isochrones: z.boolean(),
});

export type EstateAnalysisSetting = z.infer<typeof schema>;

type EstateAnalysisSettingField = keyof EstateAnalysisSetting;

const FormFields: Record<string, EstateAnalysisSettingField[]> = {
  generalPriceValuation: [
    'salesPrice',
    'rentalPrice',
    'confidence',
    'coordinates',
    'salesPriceRange',
    'rentalPriceRange',
    'currency',
  ],
  offerStatistics: ['offerStatistics'],
  offerSearch: ['offerSearch'],
  socioEconomics: ['socioEconomics'],
  pointsOfInterest: ['pointsOfInterest'],
  isochrones: ['isochrones'],
};

export type EstateAnalysisSettingModalProps = {
  defaultCheckedFields?: Record<EstateAnalysisSettingField, boolean>;
  onSubmit: (values: EstateAnalysisSetting) => void;
};

export const EstateAnalysisSettingModal: React.FC<EstateAnalysisSettingModalProps> = ({
  defaultCheckedFields,
  onSubmit,
}) => {
  const { t } = useTranslation();

  const form = useForm<EstateAnalysisSetting>({
    resolver: zodResolver(schema),
    defaultValues: defaultCheckedFields,
  });

  const handleSubmit = (data: EstateAnalysisSetting) => {
    onSubmit(data);
  };

  return (
    <ModalBase className="min-w-[425px] space-y-4 rounded-md md:min-w-[825px]">
      <div className="flex items-center justify-between">
        <p className="text-[16px] font-semibold text-primary-light">{t('settings')}</p>
        <X
          className="ml-auto cursor-pointer stroke-[4px] text-primary-light"
          onClick={closeModal}
        />
      </div>
      <FormProvider {...form}>
        <form onSubmit={form.handleSubmit(handleSubmit)}>
          <div className="max-h-[34rem] overflow-auto rounded-md border border-gray-200">
            {Object.entries(FormFields).map(([key, fields], index) => {
              return (
                <Fragment key={key}>
                  <div
                    className={cn(
                      'bg-gray-200 px-4 py-2 text-base text-primary-dark',
                      index === 0 && 'rounded-tl-md'
                    )}
                  >
                    {t(key)}
                  </div>
                  <div className="grid grid-cols-4 gap-4 p-4 [&_*]:text-primary-dark [&_button]:h-6 [&_button]:w-6 [&_button]:border-gray-200">
                    {fields.map((fieldName) => (
                      <Controller
                        key={fieldName}
                        name={fieldName}
                        control={form.control}
                        render={({
                          field: { value, onChange, ...field },
                          formState: { errors },
                        }) => (
                          <Checkbox
                            checked={value}
                            label={t(fieldName)}
                            error={Boolean(errors[fieldName]?.message)}
                            onClick={() => onChange(!value)}
                            className="text-primary-dark"
                            {...field}
                          />
                        )}
                      />
                    ))}
                  </div>
                </Fragment>
              );
            })}
          </div>
          <div className="mt-8 flex justify-center gap-2">
            <Button variant="outline" className="min-w-52" onClick={closeModal} type="submit">
              {t('cancel')}
            </Button>
            <Button variant="contained" className="min-w-52" type="submit">
              {t('submit')}
            </Button>
          </div>
        </form>
      </FormProvider>
    </ModalBase>
  );
};
